import React, { useState, useEffect } from "react";
import { FaCalendar, FaEdit, FaMapMarkerAlt, FaSave, FaTimes } from "react-icons/fa";
import { toTitleCase } from "../../utility/stringUtils";
import { useTournamentFinalizationStatus } from "../../hooks/useTournamentFinalizationStatus";
import { useTournamentAuth } from "../../utils/authorization";
import { getCurrentTournamentStatus } from "../../utils/tournamentStatus";
import { supabase } from "../../supabaseClient";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { formatDate as formatDateStandard } from "../UI/DesignStandards";
import { formatHeaderHouses, formatSquadDateRange } from "../../utility/squadSchedule";
import { requestTournamentIndexNow } from "../../utility/requestIndexNow";

const TournamentHeader = ({
  tournamentData,
  tournamentId,
  squadDates = [],
  houses = [],
}) => {
  const { status, loading, error } =
    useTournamentFinalizationStatus(tournamentId);
  const { canManage } = useTournamentAuth(tournamentId);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tournamentName, setTournamentName] = useState(tournamentData.name);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [startDate, setStartDate] = useState(tournamentData.startDate);
  const [errorMessage, setErrorMessage] = useState("");
  const [tournamentStatus, setTournamentStatus] = useState(null);

  useEffect(() => {
    const fetchTournamentStatus = async () => {
      const tournamentStatusResult = await getCurrentTournamentStatus(
        status,
        canManage,
        loading,
        error,
        tournamentId
      );
      setTournamentStatus(tournamentStatusResult);
    };

    fetchTournamentStatus();
  }, [status, canManage, loading, error, tournamentId]);

  const StatusIcon = tournamentStatus?.icon || FaCalendar;
  const dateLabel =
    formatSquadDateRange(squadDates) || formatDateStandard(tournamentData.startDate);
  const houseLabel = formatHeaderHouses(houses);
  const dateIsRange = squadDates.filter(Boolean).length > 1;

  const handleSaveName = async () => {
    try {
      const { error: saveError } = await supabase
        .from("tournaments")
        .update({ name: tournamentName })
        .eq("id", tournamentId);

      if (saveError) throw saveError;
      void requestTournamentIndexNow(tournamentId);
      setIsEditingName(false);
      if (tournamentData.onNameUpdate) {
        tournamentData.onNameUpdate(tournamentName);
      }
    } catch (saveError) {
      console.error("Error updating tournament name:", saveError);
      setErrorMessage("Failed to update tournament name");
    }
  };

  const handleCancelEdit = () => {
    setTournamentName(tournamentData.name);
    setIsEditingName(false);
  };

  const handleSaveDate = async () => {
    try {
      const { error: saveError } = await supabase
        .from("tournaments")
        .update({ startDate: startDate })
        .eq("id", tournamentId);

      if (saveError) throw saveError;
      void requestTournamentIndexNow(tournamentId);
      setIsEditingDate(false);
      if (tournamentData.onDateUpdate) {
        tournamentData.onDateUpdate(startDate);
      }
    } catch (saveError) {
      console.error("Error updating tournament start date:", saveError);
      setErrorMessage("Failed to update tournament start date");
    }
  };

  const handleCancelDateEdit = () => {
    setStartDate(tournamentData.startDate);
    setIsEditingDate(false);
  };

  return (
    <div className="card-board overflow-hidden mb-4">
      <div className="bg-brand-charcoal text-brand-cream px-6 py-6 md:px-8">
        {errorMessage ? (
          <div className="mb-3">
            <InlineAlert
              type="error"
              title="There was a problem"
              message={errorMessage}
              onClose={() => setErrorMessage("")}
            />
          </div>
        ) : null}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center mb-3 group">
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={tournamentName}
                    onChange={(e) => setTournamentName(e.target.value)}
                    className="text-4xl font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-line rounded-lg px-3 py-1 focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    placeholder="Enter tournament name"
                  />
                  <button
                    onClick={handleSaveName}
                    className="p-2 text-brand-gold hover:bg-white/10 rounded-lg transition-colors"
                    title="Save"
                  >
                    <FaSave className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 text-red-400 hover:bg-white/10 rounded-lg transition-colors"
                    title="Cancel"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <h1 className="text-4xl font-display font-bold text-brand-cream tracking-tight">
                  {toTitleCase(tournamentData.name)}
                </h1>
              )}
              {canManage && !isEditingName && (
                <button
                  onClick={() => setIsEditingName(true)}
                  className="ml-3 p-2 text-brand-mist hover:text-brand-gold hover:bg-white/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  title="Edit tournament name"
                >
                  <FaEdit className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 text-brand-mist">
              <div className="flex items-center group">
                <FaCalendar className="mr-2 text-brand-mist flex-shrink-0" />
                {isEditingDate ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      value={startDate || ""}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="font-medium bg-brand-cream text-brand-charcoal border border-brand-line rounded-lg px-2 py-1 focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                    <button
                      onClick={handleSaveDate}
                      className="p-1 text-brand-gold hover:bg-white/10 rounded-lg transition-colors"
                      title="Save"
                    >
                      <FaSave className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelDateEdit}
                      className="p-1 text-red-400 hover:bg-white/10 rounded-lg transition-colors"
                      title="Cancel"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="font-medium">{dateLabel}</span>
                )}
                {canManage && !isEditingDate && !dateIsRange && (
                  <button
                    onClick={() => setIsEditingDate(true)}
                    className="ml-2 p-1 text-brand-mist hover:text-brand-gold hover:bg-white/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    title="Edit start date"
                  >
                    <FaEdit className="w-3 h-3" />
                  </button>
                )}
              </div>
              {houseLabel ? (
                <div className="flex items-start">
                  <FaMapMarkerAlt className="mr-2 mt-0.5 text-brand-mist flex-shrink-0" />
                  <span className="font-medium">{houseLabel}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            <div className="flex flex-col items-start md:items-end">
              {tournamentStatus ? (
                <>
                  <div
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-display font-bold tracking-wide rounded-lg border ${
                      tournamentStatus.status === "Registration Open" ||
                      tournamentStatus.status === "Tournament Active"
                        ? "border-brand-gold text-brand-gold"
                        : "border-brand-line text-brand-mist"
                    }`}
                  >
                    <StatusIcon className="mr-1.5 text-xs" />
                    {tournamentStatus.status}
                  </div>
                  <p className="text-xs text-brand-mist mt-1 md:text-right max-w-xs">
                    {tournamentStatus.description}
                  </p>
                  {canManage && tournamentStatus.nextStep && (
                    <p className="text-xs text-brand-gold mt-1 md:text-right max-w-xs font-medium">
                      Next: {tournamentStatus.nextStep}
                    </p>
                  )}
                </>
              ) : (
                <div className="animate-pulse">
                  <div className="h-7 w-32 bg-brand-line/30 rounded-lg mb-2"></div>
                  <div className="h-3 w-40 bg-brand-line/30 rounded"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentHeader;
